import { Plus, Trash2 } from 'lucide-react'
import type { ExerciseEditorProps } from '@/types'

export default function TranslationEditor({ formData, setFormData, isViewMode }: ExerciseEditorProps) {
  const addAlternative = () => {
    setFormData(prev => ({ ...prev, options: [...prev.options, ''] }))
  }

  const removeAlternative = (index: number) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }))
  }

  const updateAlternative = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((o, i) => i === index ? value : o)
    }))
  }

  return (
    <div className="space-y-4">
      {/* Instrucciones */}
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-sm text-emerald-800">
          Escribe el texto en francés que el estudiante deberá traducir al español.
        </p>
      </div>

      {/* Texto fuente en frances */}
      <div className="relative">
        <label htmlFor="translation-source" className="block text-sm font-medium text-gray-700 mb-1">
          Texto en Francés (a traducir)
        </label>
        <textarea
          id="translation-source"
          value={formData.question}
          onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
          placeholder="Bonjour, je m'appelle Marie. Comment allez-vous?"
          rows={3}
          className="w-full px-3 py-2 border-2 border-[#0055A4]/20 rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] bg-[#0055A4]/10 disabled:bg-gray-100 disabled:cursor-not-allowed text-lg"
          required
          disabled={isViewMode}
        />
        <span className="absolute top-2 right-2 text-xs font-medium text-blue-400 bg-[#0055A4]/10 px-2 py-0.5 rounded">
          FR
        </span>
      </div>

      {/* Vista previa */}
      {formData.question && (
        <div className="p-4 bg-[#0055A4]/5 border-l-4 border-[#0055A4]/40 rounded-r-lg">
          <p className="text-xs font-medium text-[#0055A4] mb-1">El estudiante verá:</p>
          <p className="text-lg italic text-[#002d5c]">"{formData.question}"</p>
        </div>
      )}

      {/* Traduccion correcta */}
      <div className="relative">
        <label htmlFor="translation-answer" className="block text-sm font-medium text-gray-700 mb-1">
          Traducción Correcta (Español)
        </label>
        <input
          id="translation-answer"
          type="text"
          value={formData.correct_answer}
          onChange={(e) => setFormData(prev => ({ ...prev, correct_answer: e.target.value }))}
          placeholder="Hola, me llamo Marie. ¿Cómo están ustedes?"
          className="w-full px-3 py-2 border-2 border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-green-50/30 disabled:bg-gray-100 disabled:cursor-not-allowed"
          required
          disabled={isViewMode}
        />
        <span className="absolute top-1/2 -translate-y-1/2 right-2 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
          ES
        </span>
      </div>

      {/* Traducciones alternativas */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Traducciones Alternativas Aceptadas (opcional)
          </span>
          {!isViewMode && (
            <button
              type="button"
              onClick={addAlternative}
              className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          )}
        </div>

        {formData.options.length > 0 && (
          <div className="space-y-2">
            {formData.options.map((alt, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={alt}
                  onChange={(e) => updateAlternative(index, e.target.value)}
                  placeholder="Traducción alternativa aceptada..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={isViewMode}
                />
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={() => removeAlternative(index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}